import { Rating, State, type Grade, type ReviewLog } from 'ts-fsrs';
import { getScheduler, newCard, type KeyedCard } from './fsrsConfig.js';
import type { DeckCard, CardStateRow, SessionPlan } from './deck.js';
import { DIRECTION_KA_TO_EN } from './cardKey.js';
import { matchAnswer } from '../lesson/matchAnswer.js';
import { getKnownWords, getNounWords, getVerbWords } from '../content/knownWords.js';
import { buildQuestion, type PoolEntry, type Question } from './question.js';

/**
 * Flashcard session state machine — mirrors the lessonReducer pattern
 * (pure reducer, side effects live in the component).
 *
 * Queue rules (FLASHCARDS.md § Session shape):
 * - NEW cards first (teach something before testing anything), then due
 *   reviews (each ordered by the session plan)
 * - grading runs ts-fsrs; a card whose next due lands within the session
 *   horizon (learning steps: 1m/10m) re-enters the back of the queue
 * - the session completes when the queue drains
 *
 * Card presentation (test mode — DECISIONS.md 2026-07-16):
 * - never-seen cards (`isNew`) render as a TEACH card (front+back together,
 *   "ვსწავლობ" = Good / "უკვე ვიცი" = Easy) — no flip, no failure
 * - every other appearance is a QUESTION (choice for young cards, typed for
 *   mature ones — see question.ts). The answer decides the FSRS grade:
 *   correct/close → Good, wrong or "I don't know" → Again. Grading and the
 *   in-session requeue happen at ANSWER time (a tab close during feedback
 *   loses nothing); CONTINUE merely advances to the next card.
 */

/**
 * Learn-ahead window: a just-graded card re-enters THIS session's queue only
 * if its next due lands within this window. Sized to catch the 1-minute
 * Again/relearning step (a failed card must be re-seen before the session
 * ends) while EXCLUDING the 10-minute Good step — re-showing a card seconds
 * after a successful recall would collapse the learning-step spacing and
 * graduate cards with near-zero real exposure (adversarial-review finding).
 * A 10m-step card is simply due later today; the home screen shows the time.
 */
export const LEARN_AHEAD_MS = 2 * 60_000;

export interface SessionCard {
  key: string;
  en: string;
  ka: string;
  audio_key?: string;
  /** True until the card's very first grading (teach presentation). */
  isNew: boolean;
  card: KeyedCard;
}

/** One grading outcome, emitted for the offline persistence queue. */
export interface EmittedReview {
  card_key: string;
  direction: number;
  rating: Grade;
  reviewed_at: string;
  card: {
    due: string;
    stability: number;
    difficulty: number;
    scheduled_days: number;
    learning_steps: number;
    reps: number;
    lapses: number;
    state: State;
    last_review: string | null;
  };
  log: {
    state: State;
    due: string;
    stability: number;
    difficulty: number;
    scheduled_days: number;
    learning_steps: number;
  };
}

/** The learner's answer verdict for the feedback stage. */
export type AnswerVerdict = 'exact' | 'close' | 'wrong' | 'idk';

export interface SessionState {
  phase: 'reviewing' | 'complete';
  current: SessionCard | null;
  queue: SessionCard[];
  /** Per-card stage: answering (input live) → checked (feedback shown). */
  stage: 'answering' | 'checked';
  /** The current card's question; null for teach cards. */
  question: Question | null;
  /** Set while stage='checked' — drives the result banner + locked reveal. */
  feedback: { verdict: AnswerVerdict; given: string | null } | null;
  /** Distractor pool (full deck, deduped) — static for the session. */
  pool: PoolEntry[];
  /** Session salt for deterministic-per-appearance option layouts. */
  seed: string;
  /** Card appearances shown so far — the remount key + question salt.
   *  Increments on ADVANCE (not on answer), so the card area never
   *  remounts between an answer and its feedback. */
  seq: number;
  /** Total gradings performed (re-queued learning reps count). */
  graded: number;
  /** Unique card keys graded at least once — the XP basis. */
  reviewedKeys: string[];
  /** Unique keys that were new and got graded (session summary stat). */
  newLearnedKeys: string[];
  againCount: number;
  /** Reviews emitted so far; the component persists slice(persistedCount). */
  emitted: EmittedReview[];
  completedAt: number | null;
}

/**
 * Every action carries the `seq` of the appearance it was aimed at (read
 * from state at dispatch time). The reducer drops actions whose seq is
 * stale — a double-tapped teach button cannot grade the NEXT card sight
 * unseen (GRADE advances within one dispatch, so its second firing passes
 * the stage guard; the appearance token is what stops it).
 */
export type SessionAction =
  /** Answer the current question. `answer: null` = "I don't know" —
   *  identical grade to a wrong answer, never punished extra. */
  | { type: 'ANSWER'; answer: string | null; now: number; seq: number }
  /** Teach cards only: grade + advance in one tap. */
  | { type: 'GRADE'; rating: Grade; now: number; seq: number }
  /** Leave the feedback stage and move to the next card. */
  | { type: 'CONTINUE'; now: number; seq: number };

export function initSession(
  plan: SessionPlan,
  now: Date,
  opts: { pool: PoolEntry[]; seed: string },
): SessionState {
  // New words FIRST, reviews after (Tornike, 2026-07-16): a session opens
  // by teaching something, not with an exam on last week's cards. The
  // teach→question weave then mixes the new words' questions into the
  // review stream that follows.
  const cards = [...plan.fresh, ...plan.due].map((c) => toSessionCard(c, now));
  const [current = null, ...queue] = cards;
  return {
    phase: current ? 'reviewing' : 'complete',
    current,
    queue,
    stage: 'answering',
    question:
      current && !current.isNew
        ? buildQuestion(current, opts.pool, `${opts.seed}:${current.key}:0`)
        : null,
    feedback: null,
    pool: opts.pool,
    seed: opts.seed,
    seq: 0,
    graded: 0,
    reviewedKeys: [],
    newLearnedKeys: [],
    againCount: 0,
    emitted: [],
    completedAt: current ? null : Date.now(),
  };
}

function toSessionCard(deckCard: DeckCard, now: Date): SessionCard {
  const { entry, state } = deckCard;
  return {
    key: entry.key,
    en: entry.en,
    ka: entry.ka,
    audio_key: entry.audio_key,
    isNew: state === null,
    card: state ? stateToCard(state, entry.key) : newCard(entry.key, now),
  };
}

function stateToCard(row: CardStateRow, key: string): KeyedCard {
  const card: KeyedCard = {
    due: row.due,
    stability: row.stability,
    difficulty: row.difficulty,
    elapsed_days: 0, // deprecated in ts-fsrs, unused by the scheduler
    scheduled_days: row.scheduled_days,
    learning_steps: row.learning_steps,
    reps: row.reps,
    lapses: row.lapses,
    state: row.state,
    last_review: row.last_review ?? undefined,
    card_key: key,
  };
  return card;
}

export function sessionReducer(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case 'ANSWER': {
      if (state.phase !== 'reviewing' || action.seq !== state.seq || !state.current) return state;
      if (state.current.isNew || state.stage !== 'answering' || !state.question) return state;
      // An empty typed submit is a UI slip, not an answer — never grade it.
      if (action.answer !== null && action.answer.trim() === '') return state;

      const q = state.question.exercise;
      const verdict: AnswerVerdict =
        action.answer === null
          ? 'idk'
          : state.question.kind === 'choice'
            ? action.answer === q.answer
              ? 'exact'
              : 'wrong'
            : matchAnswer({
                input: action.answer,
                answer: q.answer,
                altAnswers: q.type === 'translate_typed' ? q.alt_answers : [],
                knownWords: getKnownWords(),
                verbs: getVerbWords(),
                nouns: getNounWords(),
              });
      // close (small typo) still counts as knowing the word.
      const rating: Grade = verdict === 'wrong' || verdict === 'idk' ? Rating.Again : Rating.Good;

      return {
        ...state,
        ...gradeCurrent(state, rating, action.now),
        stage: 'checked',
        feedback: { verdict, given: action.answer },
      };
    }

    case 'GRADE': {
      // Teach cards only — one tap grades AND advances (no feedback stage).
      if (state.phase !== 'reviewing' || action.seq !== state.seq || !state.current) return state;
      if (!state.current.isNew || state.stage !== 'answering') return state;
      return advance({ ...state, ...gradeCurrent(state, action.rating, action.now) }, action.now);
    }

    case 'CONTINUE': {
      if (state.phase !== 'reviewing' || action.seq !== state.seq) return state;
      if (state.stage !== 'checked') return state;
      return advance(state, action.now);
    }

    default:
      return state;
  }
}

/** How many other cards sit between a teach card and its first question —
 *  close enough to recall, far enough that it's memory, not the screen. */
export const TEACH_QUESTION_GAP = 2;

/** Grade the current card: schedule via FSRS, emit the review, decide the
 *  in-session requeue. Does NOT advance — the caller decides (teach cards
 *  advance immediately; questions sit in the feedback stage first). */
function gradeCurrent(
  state: SessionState,
  rating: Grade,
  nowMs: number,
): Pick<
  SessionState,
  'queue' | 'graded' | 'reviewedKeys' | 'newLearnedKeys' | 'againCount' | 'emitted'
> {
  const current = state.current!;
  const { card: updated, log } = getScheduler().next(current.card, new Date(nowMs), rating);
  const updatedKeyed: KeyedCard = { ...updated, card_key: current.key };

  const emitted: EmittedReview = toEmitted(current.key, rating, updatedKeyed, log);

  const reviewedKeys = state.reviewedKeys.includes(current.key)
    ? state.reviewedKeys
    : [...state.reviewedKeys, current.key];
  const newLearnedKeys =
    current.isNew && !state.newLearnedKeys.includes(current.key)
      ? [...state.newLearnedKeys, current.key]
      : state.newLearnedKeys;

  const queue = [...state.queue];
  const replay = { ...current, isNew: false, card: updatedKeyed };
  if (updatedKeyed.due.getTime() <= nowMs + LEARN_AHEAD_MS) {
    // Near-due cards (the 1m failure step) replay this session.
    queue.push(replay);
  } else if (current.isNew && rating === Rating.Good) {
    // The teach→question weave: a card the learner chose to LEARN gets its
    // first QUESTION a few cards later in this same session, even though
    // its 10m Good-step lands outside the learn-ahead window. In flip mode
    // that second exposure was a zero-effort glance (excluded 2026-07-14 —
    // it fake-graduated cards); in test mode it is the card's first real
    // test, and the session must not end before the word is answered.
    // "უკვე ვიცი" (Easy) stays out: it's a deliberate skip, and the card
    // meets its first test — typed — at its real review in a few days.
    queue.splice(Math.min(TEACH_QUESTION_GAP, queue.length), 0, replay);
  }

  return {
    queue,
    graded: state.graded + 1,
    reviewedKeys,
    newLearnedKeys,
    againCount: state.againCount + (rating === Rating.Again ? 1 : 0),
    emitted: [...state.emitted, emitted],
  };
}

/** Move to the next card: pop the queue, reset the stage, build the next
 *  question. The single advance path shared by GRADE and CONTINUE. */
function advance(state: SessionState, nowMs: number): SessionState {
  const [next = null, ...rest] = state.queue;
  const seq = state.seq + 1;
  return {
    ...state,
    phase: next ? 'reviewing' : 'complete',
    current: next,
    queue: rest,
    stage: 'answering',
    question:
      next && !next.isNew
        ? buildQuestion(next, state.pool, `${state.seed}:${next.key}:${seq}`)
        : null,
    feedback: null,
    seq,
    completedAt: next ? null : nowMs,
  };
}

function toEmitted(
  key: string,
  rating: Grade,
  card: KeyedCard,
  log: ReviewLog,
): EmittedReview {
  return {
    card_key: key,
    direction: DIRECTION_KA_TO_EN,
    rating,
    reviewed_at: log.review.toISOString(),
    card: {
      due: card.due.toISOString(),
      stability: card.stability,
      difficulty: card.difficulty,
      scheduled_days: card.scheduled_days,
      learning_steps: card.learning_steps,
      reps: card.reps,
      lapses: card.lapses,
      state: card.state,
      last_review: card.last_review ? new Date(card.last_review).toISOString() : null,
    },
    log: {
      state: log.state,
      due: log.due.toISOString(),
      stability: log.stability,
      difficulty: log.difficulty,
      scheduled_days: log.scheduled_days,
      learning_steps: log.learning_steps,
    },
  };
}

/** Session progress in [0,1] — graded over graded + remaining. During
 *  'checked' the current card is already graded, so it no longer counts
 *  as remaining (the bar ticks up the moment the answer lands). */
export function sessionProgress(state: SessionState): number {
  const remaining = state.queue.length + (state.current && state.stage === 'answering' ? 1 : 0);
  const total = state.graded + remaining;
  return total === 0 ? 1 : state.graded / total;
}

/** Apply an emitted review onto a cached row map (optimistic cache update). */
export function emittedToRow(review: EmittedReview, prev: CardStateRow | undefined): CardStateRow {
  return {
    card_key: review.card_key,
    direction: review.direction,
    due: new Date(review.card.due),
    stability: review.card.stability,
    difficulty: review.card.difficulty,
    scheduled_days: review.card.scheduled_days,
    learning_steps: review.card.learning_steps,
    reps: review.card.reps,
    lapses: review.card.lapses,
    state: review.card.state,
    last_review: review.card.last_review ? new Date(review.card.last_review) : null,
    suspended: prev?.suspended ?? false,
    created_at: prev?.created_at ?? new Date(review.reviewed_at),
  };
}
